import cn from 'classnames';
import { ContentTabs } from 'modules/product-details/components/content';
import { StaticPDPProduct } from 'modules/product-details/types';
import { LayoutGroup } from 'motion/react';
import React from 'react';
import { Gtm, gtm } from 'tracking/gtm';
import { slugify } from 'utils/strings';
import ProductInformationTab from './product-information-tab';
import Tab from './tab';

interface PropTypes {
  activeTab: number | null;
  className?: string;
  contentClassName?: string;
  product: StaticPDPProduct;
  tabs: ContentTabs;
  onContentTabChange: (newIndex: number | null) => void;
}

/**
 * Tabs Accordeon component that renders the tabs for the product details page.
 */
const TabsAccordeon: React.FC<PropTypes> = ({
  activeTab,
  className,
  contentClassName,
  product,
  tabs,
  onContentTabChange,
}) => (
  <div data-test="content-tabs" className={cn(className)} id="content-tabs">
    {/* Desktop Buttons */}
    <div
      className={cn(
        'border-grey-100 hidden w-screen overflow-x-auto border-t border-b whitespace-nowrap lg:mt-4 lg:flex lg:justify-center'
      )}
    >
      <div className="flex justify-center leading-none" role="tablist">
        {tabs.map((tab, index) => {
          const { title } = tab;
          return (
            <button
              data-test={`producttab-${slugify(title)}`}
              role="tab"
              aria-selected={activeTab === index ? 'true' : 'false'}
              aria-controls={`${slugify(title)}-tab`}
              id={slugify(title)}
              key={title}
              className={cn(
                'border-b-2 px-3 py-3 text-sm font-black focus:outline-hidden',
                activeTab === index || (index === 0 && activeTab === null)
                  ? 'border-primary-main text-primary-main'
                  : 'text-text-light-bg-secondary hover:text-text-light-bg-secondary border-transparent'
              )}
              onClick={() => {
                gtm({
                  group: Gtm.GroupName.Product,
                  name: Gtm.ProductEventName.InfoTabClick,
                  data: {
                    product: {
                      productTitle: product.title,
                      productId: product.id + '',
                      productType: product.productType,
                      tags: product.tags.join(','),
                      tabName: title,
                    },
                  },
                });
                onContentTabChange(
                  // This check is necessary in order not to show the content of the product information tab twice
                  // after the click (desktop only).
                  index === 0 ? null : index
                );
              }}
            >
              {title}
            </button>
          );
        })}
      </div>
    </div>

    {/* Mobile Buttons & Desktop + Mobile Tabs */}
    <LayoutGroup>
      {tabs.map((tab, index) => (
        <Tab
          key={tab.title}
          tab={tab}
          index={index}
          activeTab={activeTab}
          product={product}
          onContentTabChange={onContentTabChange}
          contentClassName={contentClassName}
        />
      ))}
    </LayoutGroup>

    <ProductInformationTab activeTab={activeTab} tabs={tabs} />
  </div>
);

export default TabsAccordeon;
